import { useState } from 'react';

import { getTeamApi } from '@/lib/api/teamsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { REG_STATUS_CONFIG, getTeamPaymentStatusConfig } from '@/lib/status-config';
import { formatDateAbbreviatedFr } from '@shared';
import { PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Clock, Phone, RotateCcw, ShieldCheck, X, XCircle } from 'lucide-react';

function initials(firstName?: string | null, lastName?: string | null): string {
  const f = (firstName ?? '').charAt(0).toUpperCase();
  const l = (lastName ?? '').charAt(0).toUpperCase();
  return `${f}${l}` || '?';
}

interface InscriptionDetailModalProps {
  teamId: number | null;
  competition: Competition | undefined;
  onClose: () => void;
  onStatusUpdate: (teamId: number, status: TeamRegistrationStatus) => void;
  updating: boolean;
  /**
   * D3 (décision 2026-05-22) : création explicite d'une RefundRequest par l'organisateur.
   * Appelé avec le paymentId du paiement COMPLETED de l'équipe + le motif saisi.
   * Si omis, le bouton n'est pas rendu.
   */
  onRequestRefund?: (paymentId: number, reason: string) => void;
  /** Loading state propagé depuis la mutation parent. */
  requestingRefund?: boolean;
}

export function InscriptionDetailModal({
  teamId,
  competition,
  onClose,
  onStatusUpdate,
  updating,
  onRequestRefund,
  requestingRefund = false,
}: InscriptionDetailModalProps) {
  // D3 : état local du formulaire "Demander un remboursement"
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const { data: team, isLoading } = useQuery({
    queryKey: queryKeys.teams.detail(teamId!),
    queryFn: () => getTeamApi(teamId!),
    enabled: teamId !== null,
  });

  if (!teamId) return null;

  const regCfg = team ? REG_STATUS_CONFIG[team.registrationStatus] : null;
  const payCfg = team ? getTeamPaymentStatusConfig(team, competition) : null;

  return (
    <div className='fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

      {/* Panel */}
      <div
        role='dialog'
        aria-modal='true'
        aria-label={team?.name ?? 'Détail équipe'}
        className='relative z-10 max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-xl sm:rounded-2xl'
      >
        {/* Header */}
        <div className='sticky top-0 flex items-start justify-between rounded-t-2xl border-b border-slate-200 bg-white px-6 py-4'>
          <div className='min-w-0 pr-4'>
            {isLoading ? (
              <div className='h-5 w-48 animate-pulse rounded bg-slate-200' />
            ) : (
              <>
                <h2 className='truncate text-lg font-bold text-slate-900'>{team?.name}</h2>
                {competition && <p className='mt-0.5 truncate text-sm text-slate-500'>{competition.name}</p>}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label='Fermer'
            className='shrink-0 rounded-lg p-2 transition-colors hover:bg-slate-100'
          >
            <X className='h-5 w-5 text-slate-500' />
          </button>
        </div>

        {/* Loading skeleton */}
        {isLoading && (
          <div className='space-y-4 p-6'>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className='h-14 animate-pulse rounded-xl bg-slate-100' />
            ))}
          </div>
        )}

        {/* Content */}
        {!isLoading && team && (
          <>
            <div className='space-y-5 p-6'>
              {/* Status badges */}
              <div className='flex flex-wrap gap-2'>
                {regCfg && (
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${regCfg.bg} ${regCfg.text}`}
                  >
                    {team.registrationStatus === TeamRegistrationStatus.CONFIRMED ? (
                      <CheckCircle className='h-3.5 w-3.5' />
                    ) : team.registrationStatus === TeamRegistrationStatus.PENDING ? (
                      <Clock className='h-3.5 w-3.5' />
                    ) : (
                      <XCircle className='h-3.5 w-3.5' />
                    )}
                    {regCfg.label}
                  </span>
                )}
                {payCfg && (
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${payCfg.bg} ${payCfg.text}`}
                  >
                    Paiement : {payCfg.label}
                  </span>
                )}
                {payCfg?.isFreeCompetition && (
                  <span className='inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600'>
                    Aucun paiement requis
                  </span>
                )}
                {!payCfg?.isFreeCompetition && team.paidAmount != null && team.paidAmount > 0 && (
                  <span className='inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700'>
                    {(team.paidAmount / 100).toFixed(2)} &euro;
                  </span>
                )}
              </div>

              {/* Captain: sporting player #1, not the admin who advanced payment. */}
              {(team.captain || team.captainPhone || team.registeredByOrganizer) && (
                <div className='rounded-xl bg-slate-50 p-4'>
                  <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500'>Capitaine</p>
                  <div className='flex items-center gap-3'>
                    <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100'>
                      <span className='text-sm font-bold text-indigo-700'>
                        {initials(
                          team.registeredByOrganizer
                            ? team.players?.find(p => p.playerOrder === 1)?.firstName
                            : team.captain?.firstName,
                          team.registeredByOrganizer
                            ? team.players?.find(p => p.playerOrder === 1)?.lastName
                            : team.captain?.lastName,
                        )}
                      </span>
                    </div>
                    <div className='min-w-0'>
                      {team.registeredByOrganizer ? (
                        <>
                          <p className='truncate font-semibold text-slate-900'>
                            {team.players?.find(p => p.playerOrder === 1)?.firstName}{' '}
                            {team.players?.find(p => p.playerOrder === 1)?.lastName}
                          </p>
                          {team.players?.find(p => p.playerOrder === 1)?.licenseNumber && (
                            <p className='truncate text-sm text-slate-500'>
                              Licence : {team.players?.find(p => p.playerOrder === 1)?.licenseNumber}
                            </p>
                          )}
                          <p className='text-xs text-blue-700'>
                            Payeur : {team.captain?.firstName} {team.captain?.lastName} ({team.captain?.email})
                          </p>
                        </>
                      ) : (
                        <>
                          {team.captain && (
                            <p className='truncate font-semibold text-slate-900'>
                              {team.captain.firstName} {team.captain.lastName}
                            </p>
                          )}
                          {team.captain?.email && (
                            <p className='truncate text-sm text-slate-500'>{team.captain.email}</p>
                          )}
                          {team.captain?.licenseNumber && (
                            <p className='truncate text-sm text-slate-500'>Licence : {team.captain.licenseNumber}</p>
                          )}
                        </>
                      )}
                      {team.captainPhone && (
                        <p className='mt-0.5 flex items-center gap-1 text-sm text-slate-500'>
                          <Phone className='h-3.5 w-3.5 shrink-0' />
                          {team.captainPhone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Players */}
              {team.players && team.players.length > 0 && (
                <div>
                  <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500'>
                    Joueurs ({team.players.length})
                  </p>
                  <div className='space-y-2'>
                    {[...team.players]
                      .sort((a, b) => a.playerOrder - b.playerOrder)
                      .map(player => (
                        <div key={player.id} className='flex items-center gap-3 rounded-xl bg-slate-50 p-3'>
                          <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200'>
                            <span className='text-xs font-bold text-slate-600'>{player.playerOrder}</span>
                          </div>
                          <div className='min-w-0 flex-1'>
                            <p className='truncate font-medium text-slate-900'>
                              {player.firstName} {player.lastName}
                            </p>
                            {player.licenseNumber && (
                              <p className='text-xs text-slate-400'>Licence : {player.licenseNumber}</p>
                            )}
                            {player.licenseAutoDeclaredAt ? (
                              <p className='mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-emerald-600'>
                                <ShieldCheck className='h-3 w-3 shrink-0' />
                                Déclarée sur l&apos;honneur le{' '}
                                {new Date(player.licenseAutoDeclaredAt).toLocaleString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            ) : (
                              player.licenseNumber && (
                                <p className='text-xs text-slate-400'>Déclaration non disponible (ancien système)</p>
                              )
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {team.notes && (
                <div className='rounded-xl bg-amber-50 p-4'>
                  <p className='mb-1 text-xs font-semibold text-amber-700'>Notes</p>
                  <p className='text-sm text-amber-900'>{team.notes}</p>
                </div>
              )}

              {/* Payment date */}
              {!payCfg?.isFreeCompetition && team.paidAt && (
                <p className='text-xs text-slate-400'>Pay&eacute; le {formatDateAbbreviatedFr(team.paidAt)}</p>
              )}

              {/* Registered at */}
              <p className='text-xs text-slate-400'>Inscrit le {formatDateAbbreviatedFr(team.createdAt)}</p>
            </div>

            {/* Footer actions */}
            {team.registrationStatus === TeamRegistrationStatus.PENDING && (
              <div className='sticky bottom-0 flex gap-3 border-t border-slate-200 bg-white px-6 py-4'>
                <button
                  onClick={() => onStatusUpdate(team.id, TeamRegistrationStatus.CONFIRMED)}
                  disabled={updating}
                  className='flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50'
                >
                  <CheckCircle className='h-4 w-4' />
                  Confirmer
                </button>
                <button
                  onClick={() => onStatusUpdate(team.id, TeamRegistrationStatus.REJECTED)}
                  disabled={updating}
                  className='flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50'
                >
                  <XCircle className='h-4 w-4' />
                  Rejeter
                </button>
              </div>
            )}
            {team.registrationStatus === TeamRegistrationStatus.CONFIRMED && (
              <div className='sticky bottom-0 border-t border-slate-200 bg-white px-6 py-4 space-y-3'>
                {team.paymentStatus === PaymentStatus.COMPLETED && (
                  <div className='flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 p-3'>
                    <AlertTriangle className='h-4 w-4 text-amber-600 mt-0.5 shrink-0' />
                    <p className='text-sm text-amber-800'>
                      Cette équipe a payé{' '}
                      <strong>{team.paidAmount != null ? `${(team.paidAmount / 100).toFixed(2)} €` : ''}</strong>. Une
                      demande de remboursement sera automatiquement créée.
                    </p>
                  </div>
                )}

                {/* D3 (décision 2026-05-22) : bouton explicite "Demander un remboursement".
                    Affiché uniquement si :
                      - le paiement est COMPLETED
                      - aucune RefundRequest n'existe déjà pour ce paiement
                      - le handler onRequestRefund a été fourni par le parent.
                    Le paymentId est récupéré depuis team.payments[] (paiement COMPLETED). */}
                {team.paymentStatus === PaymentStatus.COMPLETED &&
                  onRequestRefund &&
                  !team.refundRequests?.[0] &&
                  (() => {
                    const completedPayment = team.payments?.find(p => p.status === PaymentStatus.COMPLETED);
                    if (!completedPayment) return null;

                    if (!showRefundForm) {
                      return (
                        <button
                          onClick={() => setShowRefundForm(true)}
                          disabled={requestingRefund}
                          className='flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-300 bg-indigo-50 py-2.5 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:opacity-50'
                        >
                          <RotateCcw className='h-4 w-4' />
                          Demander un remboursement
                        </button>
                      );
                    }

                    return (
                      <div className='space-y-2 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3'>
                        <label className='block text-xs font-semibold text-indigo-900'>Motif de la demande</label>
                        <textarea
                          value={refundReason}
                          onChange={e => setRefundReason(e.target.value)}
                          placeholder='Précisez le motif (sera envoyé aux SUPER_ADMIN par email)…'
                          rows={3}
                          maxLength={500}
                          className='w-full rounded-lg border border-indigo-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none'
                        />
                        <p className='text-xs text-slate-500'>{refundReason.length} / 500</p>
                        <div className='flex gap-2'>
                          <button
                            onClick={() => {
                              setShowRefundForm(false);
                              setRefundReason('');
                            }}
                            disabled={requestingRefund}
                            className='flex-1 rounded-xl border border-slate-300 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50'
                          >
                            Annuler
                          </button>
                          <button
                            onClick={() => {
                              if (refundReason.trim().length < 10) return;
                              onRequestRefund(completedPayment.id, refundReason.trim());
                              setShowRefundForm(false);
                              setRefundReason('');
                            }}
                            disabled={requestingRefund || refundReason.trim().length < 10}
                            className='flex-1 rounded-xl bg-indigo-600 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50'
                          >
                            {requestingRefund ? 'Envoi…' : 'Envoyer la demande'}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                <button
                  onClick={() => onStatusUpdate(team.id, TeamRegistrationStatus.CANCELLED)}
                  disabled={updating}
                  className='flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50'
                >
                  <XCircle className='h-4 w-4' />
                  Annuler l&apos;inscription
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
